import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, serverTimestamp, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { useAuth } from '../../context/AuthContext';
import { useAdminSettings, useAllProductionAreas, useFloorWorkerRates, useAllTaskTags } from '../../hooks/useCompanyData';
import { useBestRunBenchmarks } from '../../hooks/useBestRunBenchmarks';
import { getDateRange } from '../../utils/time';
import { formatDuration, formatCurrency, calcTaskMetrics } from '../../utils/labour';
import { formatDateTime, formatTime, toDatetimeLocal, fromDatetimeLocal } from '../../utils/time';
import { calcActiveElapsedMinutes, estimateActiveLabourCost } from '../../utils/replayLive';
import { getBenchmarkKey } from '../../utils/benchmark';
import {
  taskHasMissingData,
  getHighCostThreshold,
  taskIsHighCost,
  getTaskReviewInfo,
} from '../../utils/taskReview';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useToast } from '../../components/common/Toast';
import {
  Search, Edit, Archive, XCircle, X, AlertCircle, Info, StickyNote,
  Activity, Clock, Users, MapPin, MoreVertical, RotateCcw, ClipboardList,
} from 'lucide-react';
import TaskNotes from '../../components/common/TaskNotes';
import TaskTagChips from '../../components/common/TaskTagChips';
import TaskTagPicker from '../../components/common/TaskTagPicker';
import ExportButton from '../../components/common/ExportButton';
import { buildProductionTasksCSV } from '../../utils/exportHelpers';
import { resolveTaskTagFields, recordRecentTagIds, DEFAULT_TAG_COLOR, normalizeTagName } from '../../utils/taskTags';
import { downloadCSV, makeExportFilename } from '../../utils/csv';
import { logProductionEvent, PRODUCTION_EVENT_TYPES } from '../../utils/productionEvents';
import { getTaskEditChangedFields, normalizeTaskEditSnapshot } from '../../utils/productionReplay';
import './AdminTasksPage.css';

const DATE_PRESETS = ['today', 'yesterday', 'thisWeek', 'thisMonth'];
const QUICK_FILTERS = ['activeOnly', 'needsReview', 'highCost', 'missingData'];
const LIVE_TICK_MS = 10000;

const toTime = (raw) => {
  const d = raw?.toDate?.() ?? (raw ? new Date(raw) : null);
  if (!d) return null;
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

function getTaskDisplayName(task) {
  return (task.isCustomTaskName && task.customTaskName) ? task.customTaskName : task.taskName;
}

function formatWorkerLabel(task) {
  if (task.employeeNames?.length === 1) return task.employeeNames[0];
  if (task.employeeNames?.length > 1) return `${task.employeeNames[0]} +${task.employeeNames.length - 1}`;
  if (task.teamSize > 1) return `${task.teamSize} workers`;
  if (task.teamSize === 1) return null;
  return null;
}

function TaskActions({ task, t, onEdit, onNotes, onArchive, onAbandon, compact = false }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const items = [
    { key: 'edit', label: t('admin.editTask'), icon: Edit, onClick: () => onEdit(task), danger: false },
    { key: 'notes', label: t('tasksPage.viewNotes'), icon: StickyNote, onClick: () => onNotes(task), danger: false },
  ];
  if (task.status === 'completed') {
    items.push({ key: 'archive', label: t('admin.archiveTask'), icon: Archive, onClick: () => onArchive(task), danger: false });
  }
  if (task.status === 'active') {
    items.push({ key: 'abandon', label: t('admin.abandonTask'), icon: XCircle, onClick: () => onAbandon(task), danger: true });
  }

  if (compact) {
    return (
      <div className="tasks-actions-menu" ref={menuRef}>
        <button
          type="button"
          className="btn btn-icon btn-sm"
          aria-label={t('taskTable.actions')}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <MoreVertical size={16} />
        </button>
        {open && (
          <div className="tasks-actions-dropdown" role="menu">
            {items.map(({ key, label, icon: Icon, onClick, danger }) => (
              <button
                key={key}
                type="button"
                role="menuitem"
                className={`tasks-actions-dropdown-item${danger ? ' tasks-actions-dropdown-item--danger' : ''}`}
                onClick={() => { onClick(); setOpen(false); }}
              >
                <Icon size={14} aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tasks-actions-desktop">
      {items.map(({ key, label, icon: Icon, onClick, danger }) => (
        <button
          key={key}
          type="button"
          className={`btn btn-icon btn-sm${danger ? ' btn-danger-ghost' : ''}`}
          title={label}
          aria-label={label}
          onClick={onClick}
        >
          <Icon size={14} aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}

function ActiveTaskCard({ task, liveView, currency, t, onEdit, onNotes, onAbandon }) {
  const isPaused = Boolean(task.pausedAt);
  const worker = formatWorkerLabel(task);
  const reviewBadge = liveView?.warnings?.find((w) => w.tone === 'warning');

  return (
    <li className={`tasks-active-card${reviewBadge ? ' tasks-active-card--review' : ''}`}>
      <div className="tasks-active-card-header">
        <span className="tasks-active-card-name">{getTaskDisplayName(task)}</span>
        <span className={`tasks-active-card-status tasks-active-card-status--${isPaused ? 'paused' : 'running'}`}>
          {isPaused ? t('production.onBreak') : t('common.active')}
        </span>
      </div>
      <ul className="tasks-active-card-meta">
        {worker && (
          <li><Users size={12} aria-hidden="true" /><span>{worker}</span></li>
        )}
        {task.roomName && (
          <li><MapPin size={12} aria-hidden="true" /><span>{task.roomName}</span></li>
        )}
        {liveView?.startTimeLabel && (
          <li><Clock size={12} aria-hidden="true" /><span>{liveView.startTimeLabel}</span></li>
        )}
      </ul>
      <div className="tasks-active-card-stats">
        <span className="tasks-active-card-elapsed" aria-live="polite">
          {liveView?.elapsedMinutes != null ? formatDuration(liveView.elapsedMinutes) : '—'}
        </span>
        {liveView?.estimatedCost != null && (
          <span className="tasks-active-card-cost">
            {formatCurrency(liveView.estimatedCost, currency)}
            <span className="tasks-active-card-est">{t('tasksPage.estimated')}</span>
          </span>
        )}
      </div>
      {reviewBadge && (
        <p className="tasks-active-card-review">
          <AlertCircle size={13} aria-hidden="true" />
          {reviewBadge.label}
        </p>
      )}
      <div className="tasks-active-card-actions">
        <TaskActions
          task={task}
          t={t}
          onEdit={onEdit}
          onNotes={onNotes}
          onArchive={() => {}}
          onAbandon={onAbandon}
        />
      </div>
    </li>
  );
}

function TaskTableRow({
  task, currency, t, benchmark, tagsById, onEdit, onNotes, onArchive, onAbandon,
}) {
  const isOnBreak = task.status === 'active' && !!task.pausedAt;
  const taskDisplayName = getTaskDisplayName(task);
  const review = getTaskReviewInfo(task, { benchmark });
  const worker = formatWorkerLabel(task);
  const startDate = task.startTime ? formatDateTime(task.startTime.toDate?.() ?? task.startTime) : null;
  const endTimeStr = task.endTime ? toTime(task.endTime) : null;
  const timingSub = [
    endTimeStr ? `→ ${endTimeStr}` : null,
    task.durationMinutes != null ? formatDuration(task.durationMinutes) : null,
  ].filter(Boolean).join(' · ');

  const labourHoursStr = task.labourHours != null ? `${task.labourHours.toFixed(1)}h` : null;
  const outputSub = [task.productName, task.lotNumber].filter(Boolean).join(' · ');
  const warningLabel = review.warnings.find((w) => w.tone === 'warning')?.label
    ?? review.warnings.find((w) => w.key === 'cost_per_unit')?.label;

  let costDisplay;
  if (task.labourCost != null) {
    costDisplay = formatCurrency(task.labourCost, currency);
  } else if (task.labourRateSource === 'partialRates') {
    costDisplay = (
      <span className="cell-cost-partial" title={t('tasksPage.partialRates')}>
        {t('tasksPage.notRecorded')} <AlertCircle size={11} aria-hidden="true" />
      </span>
    );
  } else if (task.labourRateSource === 'unavailable') {
    costDisplay = <span className="text-muted" title={t('tasksPage.noRateConfigured')}>{t('tasksPage.notRecorded')}</span>;
  } else {
    costDisplay = <span className="text-muted">{t('tasksPage.notRecorded')}</span>;
  }

  let costUnitDisplay;
  if (task.costPerUnit != null) {
    costUnitDisplay = formatCurrency(task.costPerUnit, currency);
  } else if (task.labourCost != null && !task.quantityCompleted) {
    costUnitDisplay = (
      <span className="tasks-cell-warning" title={t('tasksPage.costUnitUnavailable')}>
        {t('tasksPage.costUnitUnavailable')}
      </span>
    );
  } else {
    costUnitDisplay = null;
  }

  return (
    <tr className={`tasks-row tasks-row--${task.status}${review.needsReview ? ' tasks-row--review' : ''}`}>
      <td>
        <span className={`badge badge-${isOnBreak ? 'paused' : task.status}`}>
          {isOnBreak ? t('production.onBreak') : t(`common.${task.status}`)}
        </span>
      </td>
      <td>
        <span className="cell-task-name">{taskDisplayName}</span>
        <div className="cell-task-badges">
          {task.taskCategoryName && task.taskCategoryName.toLowerCase() !== taskDisplayName.toLowerCase() && (
            <span className={`badge badge-${task.taskType}`}>{task.taskCategoryName}</span>
          )}
          {task.isCustomTaskName && (
            <span className="badge badge-custom">{t('admin.customTaskFlag')}</span>
          )}
        </div>
        {warningLabel && (
          <span className="tasks-row-warning">
            <AlertCircle size={11} aria-hidden="true" />
            {warningLabel}
          </span>
        )}
        <TaskTagChips task={task} tagsById={tagsById} maxVisible={2} className="tasks-row-tags" />
      </td>
      <td>
        <span className="cell-timing-start">{startDate ?? t('tasksPage.notRecorded')}</span>
        {timingSub && <span className="cell-timing-sub">{timingSub}</span>}
      </td>
      <td>
        <div className="cell-labour">
          <span className="cell-labour-cost">{costDisplay}</span>
          {labourHoursStr && <span className="cell-labour-sub">{labourHoursStr}</span>}
        </div>
      </td>
      <td className="col-costunit">
        {costUnitDisplay ?? <span className="text-muted">{t('tasksPage.notRecorded')}</span>}
      </td>
      <td className="col-output">
        {task.quantityCompleted != null ? (
          <div className="cell-output">
            <span className="cell-output-qty">
              {task.quantityCompleted}{task.unitType ? ` ${task.unitType}` : ''}
            </span>
            {outputSub && <span className="cell-output-sub">{outputSub}</span>}
          </div>
        ) : task.productName ? (
          <span className="cell-output-sub">{task.productName}</span>
        ) : (
          <span className="text-muted">{t('tasksPage.notRecorded')}</span>
        )}
      </td>
      <td className="col-meta">
        <div className="cell-meta">
          {worker ? (
            <span className="cell-meta-worker" title={task.employeeNames?.join(', ')}>{worker}</span>
          ) : (
            <span className="text-muted">{t('tasksPage.notRecorded')}</span>
          )}
          {task.roomName ? (
            <span className="cell-meta-room">{task.roomName}</span>
          ) : (
            <span className="cell-meta-room text-muted">{t('tasksPage.notRecorded')}</span>
          )}
        </div>
      </td>
      <td>
        <TaskActions
          task={task}
          t={t}
          onEdit={onEdit}
          onNotes={onNotes}
          onArchive={onArchive}
          onAbandon={onAbandon}
        />
      </td>
    </tr>
  );
}

function TaskMobileCard({
  task, currency, t, benchmark, tagsById, onEdit, onNotes, onArchive, onAbandon,
}) {
  const isOnBreak = task.status === 'active' && !!task.pausedAt;
  const taskDisplayName = getTaskDisplayName(task);
  const review = getTaskReviewInfo(task, { benchmark });
  const worker = formatWorkerLabel(task);
  const startDate = task.startTime ? formatDateTime(task.startTime.toDate?.() ?? task.startTime) : null;
  const warningLabel = review.warnings.find((w) => w.tone === 'warning')?.label;

  return (
    <article className={`tasks-mobile-card tasks-row--${task.status}${review.needsReview ? ' tasks-row--review' : ''}`}>
      <div className="tasks-mobile-card-top">
        <span className={`badge badge-${isOnBreak ? 'paused' : task.status}`}>
          {isOnBreak ? t('production.onBreak') : t(`common.${task.status}`)}
        </span>
        <TaskActions
          task={task}
          t={t}
          onEdit={onEdit}
          onNotes={onNotes}
          onArchive={onArchive}
          onAbandon={onAbandon}
          compact
        />
      </div>
      <h3 className="tasks-mobile-card-name">{taskDisplayName}</h3>
      <TaskTagChips task={task} tagsById={tagsById} maxVisible={3} className="tasks-mobile-card-tags" />
      {warningLabel && (
        <p className="tasks-mobile-card-warning">
          <AlertCircle size={12} aria-hidden="true" />
          {warningLabel}
        </p>
      )}
      <dl className="tasks-mobile-card-grid">
        <div>
          <dt>{t('taskTable.startTime')}</dt>
          <dd>{startDate ?? t('tasksPage.notRecorded')}</dd>
        </div>
        <div>
          <dt>{t('taskTable.labourCost')}</dt>
          <dd>
            {task.labourCost != null
              ? formatCurrency(task.labourCost, currency)
              : t('tasksPage.notRecorded')}
          </dd>
        </div>
        <div>
          <dt>{t('taskTable.product')}</dt>
          <dd>{task.productName ?? t('tasksPage.notRecorded')}</dd>
        </div>
        <div>
          <dt>{t('taskTable.quantity')}</dt>
          <dd>
            {task.quantityCompleted != null
              ? `${task.quantityCompleted}${task.unitType ? ` ${task.unitType}` : ''}`
              : t('tasksPage.notRecorded')}
          </dd>
        </div>
        <div>
          <dt>{t('taskTable.worker')}</dt>
          <dd>{worker ?? t('tasksPage.notRecorded')}</dd>
        </div>
        <div>
          <dt>{t('taskTable.room')}</dt>
          <dd>{task.roomName ?? t('tasksPage.notRecorded')}</dd>
        </div>
      </dl>
    </article>
  );
}

export default function AdminTasksPage() {
  const { t, i18n } = useTranslation();
  const { user, companyId } = useAuth();
  const { adminSettings } = useAdminSettings();
  const { areas } = useAllProductionAreas();
  const { tags: allTags } = useAllTaskTags();
  const { rates: workerRates } = useFloorWorkerRates();
  const { benchmarks } = useBestRunBenchmarks();
  const toast = useToast();

  const [datePreset, setDatePreset] = useState('today');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCustom, setFilterCustom] = useState(false);
  const [filterTagId, setFilterTagId] = useState('');
  const [quickFilter, setQuickFilter] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [liveTick, setLiveTick] = useState(0);
  const [editTask, setEditTask] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editReason, setEditReason] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [abandonTarget, setAbandonTarget] = useState(null);
  const [abandoning, setAbandoning] = useState(false);
  const [notesTask, setNotesTask] = useState(null);

  const dateRange = useMemo(() => getDateRange(datePreset), [datePreset]);
  const currency = adminSettings?.labourCostCurrency || 'CAD';
  const defaultTeamRate = adminSettings?.defaultTeamHourlyRate ?? null;

  const workerRateMap = useMemo(() => {
    const m = {};
    for (const [id, rateData] of Object.entries(workerRates)) {
      if (rateData.hourlyRate != null) m[id] = rateData.hourlyRate;
    }
    return m;
  }, [workerRates]);

  const tagsById = useMemo(() => new Map(allTags.map((tg) => [tg.id, tg])), [allTags]);

  const tagUsageCounts = useMemo(() => {
    const counts = {};
    for (const task of tasks) {
      for (const id of task.tagIds || []) {
        counts[id] = (counts[id] || 0) + 1;
      }
    }
    return counts;
  }, [tasks]);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    setLoadError(false);
    const constraints = [
      where('companyId', '==', companyId),
      where('archived', '==', false),
    ];
    if (filterStatus !== 'all') constraints.push(where('status', '==', filterStatus));

    const q = query(collection(db, COLLECTIONS.PRODUCTION_TASKS), ...constraints, orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => {
      setTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
      setLoadError(false);
    }, (err) => {
      console.error('[AdminTasksPage] Firestore error:', err);
      setLoading(false);
      setLoadError(true);
    });
  }, [companyId, filterStatus]);

  const filteredTasks = useMemo(() => {
    let result = tasks;

    if (filterStatus !== 'active' && filterStatus !== 'abandoned') {
      result = result.filter((task) => {
        if (task.status === 'active' || task.status === 'abandoned') return true;
        const ts = task.submittedAt?.toDate?.() ?? (task.submittedAt ? new Date(task.submittedAt) : null);
        if (!ts) return false;
        return ts >= dateRange.start && ts <= dateRange.end;
      });
    }

    if (filterCustom) {
      result = result.filter((task) => task.isCustomTaskName === true);
    }

    if (filterTagId) {
      result = result.filter((task) => task.tagIds?.includes(filterTagId));
    }

    if (search) {
      const s = search.toLowerCase();
      result = result.filter((task) =>
        task.taskName?.toLowerCase().includes(s) ||
        task.customTaskName?.toLowerCase().includes(s) ||
        task.lotNumber?.toLowerCase().includes(s) ||
        task.productName?.toLowerCase().includes(s) ||
        task.sku?.toLowerCase().includes(s) ||
        task.roomName?.toLowerCase().includes(s) ||
        task.notes?.toLowerCase().includes(s) ||
        task.tagNames?.some((n) => n.toLowerCase().includes(s)) ||
        task.employeeNames?.some((n) => n.toLowerCase().includes(s))
      );
    }

    return result;
  }, [tasks, search, dateRange, filterStatus, filterCustom, filterTagId]);

  const highCostThreshold = useMemo(() => getHighCostThreshold(filteredTasks), [filteredTasks]);

  const quickFilteredTasks = useMemo(() => {
    if (!quickFilter) return filteredTasks;
    return filteredTasks.filter((task) => {
      if (quickFilter === 'activeOnly') return task.status === 'active';
      if (quickFilter === 'needsReview') return getTaskReviewInfo(task, { benchmark: benchmarks?.get?.(getBenchmarkKey(task)) }).needsReview;
      if (quickFilter === 'highCost') return taskIsHighCost(task, highCostThreshold);
      if (quickFilter === 'missingData') return taskHasMissingData(task);
      return true;
    });
  }, [filteredTasks, quickFilter, benchmarks, highCostThreshold]);

  const activeTasks = useMemo(
    () => filteredTasks.filter((task) => task.status === 'active'),
    [filteredTasks],
  );

  const historyTasks = useMemo(
    () => quickFilteredTasks.filter((task) => task.status !== 'active'),
    [quickFilteredTasks],
  );

  const hasActiveTasks = activeTasks.length > 0;

  useEffect(() => {
    if (!hasActiveTasks) return undefined;
    const timer = setInterval(() => setLiveTick((n) => n + 1), LIVE_TICK_MS);
    return () => clearInterval(timer);
  }, [hasActiveTasks]);

  const liveActiveViews = useMemo(() => {
    const asOf = new Date();
    return activeTasks.map((task) => {
      const elapsedMinutes = calcActiveElapsedMinutes(task, asOf);
      const benchmark = benchmarks?.get?.(getBenchmarkKey(task)) ?? null;
      const { cost } = estimateActiveLabourCost(task, elapsedMinutes, workerRateMap, defaultTeamRate);
      const review = getTaskReviewInfo(task, { benchmark, asOf });
      const start = task.startTime?.toDate?.() ?? (task.startTime ? new Date(task.startTime) : null);
      return {
        task,
        elapsedMinutes,
        estimatedCost: cost,
        startTimeLabel: start ? formatTime(start) : null,
        warnings: review.warnings,
      };
    }).sort((a, b) => (b.elapsedMinutes ?? 0) - (a.elapsedMinutes ?? 0));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTasks, workerRateMap, defaultTeamRate, benchmarks, liveTick, i18n.language]);

  const summaryStats = useMemo(() => {
    const activeRunning = filteredTasks.filter((tk) => tk.status === 'active' && !tk.pausedAt).length;
    const onBreakCount = filteredTasks.filter((tk) => tk.status === 'active' && !!tk.pausedAt).length;
    const completedCount = filteredTasks.filter((tk) => tk.status === 'completed').length;
    const tasksWithCost = filteredTasks.filter((tk) => tk.labourCost != null);
    const totalCost = tasksWithCost.reduce((s, tk) => s + tk.labourCost, 0);
    const totalHours = filteredTasks.reduce((s, tk) => s + (tk.labourHours ?? 0), 0);
    const avgCost = tasksWithCost.length > 0 ? totalCost / tasksWithCost.length : null;

    const costUnitTasks = filteredTasks.filter((tk) => tk.costPerUnit != null);
    const avgCostPerUnit = costUnitTasks.length > 0
      ? costUnitTasks.reduce((s, tk) => s + tk.costPerUnit, 0) / costUnitTasks.length
      : null;

    const needsReviewCount = filteredTasks.filter((tk) =>
      getTaskReviewInfo(tk, { benchmark: benchmarks?.get?.(getBenchmarkKey(tk)) }).needsReview,
    ).length;

    const longestActive = liveActiveViews[0]?.elapsedMinutes ?? null;

    return {
      activeRunning,
      onBreakCount,
      completedCount,
      totalCost,
      totalHours,
      hasCost: tasksWithCost.length > 0,
      avgCost,
      avgCostPerUnit,
      needsReviewCount,
      longestActive,
      totalCount: filteredTasks.length,
    };
  }, [filteredTasks, benchmarks, liveActiveViews]);

  const hasFiltersActive = search
    || filterCustom
    || filterTagId
    || filterStatus !== 'all'
    || datePreset !== 'today'
    || quickFilter !== '';

  const resetFilters = useCallback(() => {
    setSearch('');
    setFilterCustom(false);
    setFilterTagId('');
    setFilterStatus('all');
    setDatePreset('today');
    setQuickFilter('');
  }, []);

  const toggleQuickFilter = (key) => {
    setQuickFilter((prev) => (prev === key ? '' : key));
  };

  const emptyState = useMemo(() => {
    if (loading || loadError) return null;
    if (filteredTasks.length > 0) return null;
    if (tasks.length === 0) return { key: 'emptyAll', sub: 'emptyAllSub' };
    if (hasFiltersActive) return { key: 'emptyFiltered', sub: 'emptyFilteredSub' };
    return { key: 'emptyDate', sub: 'emptyDateSub' };
  }, [loading, loadError, filteredTasks.length, tasks.length, hasFiltersActive]);

  const openEdit = (task) => {
    setEditTask(task);
    setEditForm({
      taskName: task.taskName || '',
      customTaskName: task.customTaskName || '',
      productName: task.productName || '',
      sku: task.sku || '',
      lotNumber: task.lotNumber || '',
      quantityCompleted: task.quantityCompleted ?? '',
      unitType: task.unitType || '',
      teamSize: task.teamSize || 1,
      startTime: task.startTime ? toDatetimeLocal(task.startTime.toDate?.() ?? task.startTime) : '',
      endTime: task.endTime ? toDatetimeLocal(task.endTime.toDate?.() ?? task.endTime) : '',
      roomId: task.roomId || '',
      roomName: task.roomName || '',
      notes: task.notes || '',
      downtimeMinutes: task.downtimeMinutes ?? '',
      downtimeReason: task.downtimeReason || '',
      tagIds: task.tagIds || [],
    });
    setEditReason('');
    setEditErrors({});
  };

  const validateEdit = () => {
    const e = {};
    if (!editReason.trim()) e.editReason = t('validation.editReasonRequired');
    if (editForm.teamSize !== '' && Number(editForm.teamSize) < 1)
      e.teamSize = t('validation.teamSizeMin');
    if (editForm.startTime && editForm.endTime) {
      const start = fromDatetimeLocal(editForm.startTime);
      const end = fromDatetimeLocal(editForm.endTime);
      if (end <= start) {
        e.endTime = t('validation.endBeforeStart');
      } else if (editForm.downtimeMinutes !== '' && Number(editForm.downtimeMinutes) > (end - start) / 1000 / 60) {
        e.downtimeMinutes = t('validation.downtimeExceedsDuration');
      }
    }
    setEditErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleEditSave = async () => {
    if (!validateEdit()) return;
    setEditLoading(true);
    try {
      const { getDocs, addDoc } = await import('firebase/firestore');

      const ratesSnap = await getDocs(
        query(collection(db, COLLECTIONS.FLOOR_WORKER_RATES), where('companyId', '==', companyId))
      );
      const userRates = {};
      ratesSnap.forEach((d) => {
        const data = d.data();
        if (data.hourlyRate != null) userRates[d.id] = data.hourlyRate;
      });

      const defaultTeamRateLocal = adminSettings?.defaultTeamHourlyRate ?? null;
      const currencyLocal = adminSettings?.labourCostCurrency ?? 'CAD';

      const start = fromDatetimeLocal(editForm.startTime);
      const end = fromDatetimeLocal(editForm.endTime);
      const teamSize = Number(editForm.teamSize) || editTask.teamSize;
      const qty = editForm.quantityCompleted !== '' ? Number(editForm.quantityCompleted) : null;

      const editedTaskSnapshot = {
        ...editTask,
        startTime: start ?? editTask.startTime,
        endTime: end ?? editTask.endTime,
        teamSize,
        employeeIds: editTask.employeeIds,
        quantityCompleted: qty,
        downtimeMinutes: editForm.downtimeMinutes !== '' ? Number(editForm.downtimeMinutes) : null,
      };

      const metrics = calcTaskMetrics(editedTaskSnapshot, userRates, defaultTeamRateLocal, currencyLocal);
      const tagFields = resolveTaskTagFields(editForm.tagIds || [], tagsById);

      const before = normalizeTaskEditSnapshot({
        taskName: editTask.taskName,
        productName: editTask.productName,
        quantityCompleted: editTask.quantityCompleted,
        startTime: editTask.startTime,
        endTime: editTask.endTime,
        teamSize: editTask.teamSize,
        labourCost: editTask.labourCost,
        labourRateSource: editTask.labourRateSource,
      });

      const afterSnapshot = normalizeTaskEditSnapshot({
        taskName: editForm.taskName,
        productName: editForm.productName,
        quantityCompleted: qty,
        startTime: editForm.startTime,
        endTime: editForm.endTime,
        teamSize,
        labourCost: metrics.labourCost,
        labourRateSource: metrics.labourRateSource,
      });

      const changedFields = getTaskEditChangedFields(before, afterSnapshot);

      await updateDoc(doc(db, COLLECTIONS.PRODUCTION_TASKS, editTask.id), {
        taskName: editForm.taskName || editTask.taskName,
        customTaskName: editForm.customTaskName?.trim() || null,
        isCustomTaskName: !!(editForm.customTaskName?.trim()),
        productName: editForm.productName || null,
        sku: editForm.sku || null,
        lotNumber: editForm.lotNumber || null,
        quantityCompleted: qty,
        unitType: editForm.unitType || null,
        teamSize,
        startTime: start ? Timestamp.fromDate(start) : editTask.startTime,
        endTime: end ? Timestamp.fromDate(end) : editTask.endTime,
        roomId: editForm.roomId || null,
        roomName: editForm.roomName || null,
        notes: editForm.notes || null,
        downtimeMinutes: editForm.downtimeMinutes !== '' ? Number(editForm.downtimeMinutes) : null,
        downtimeReason: editForm.downtimeReason || null,
        tagIds: tagFields.tagIds,
        tagNames: tagFields.tagNames,
        ...metrics,
        editedBy: user.uid,
        editedAt: serverTimestamp(),
        editReason: editReason.trim(),
        updatedAt: serverTimestamp(),
      });

      recordRecentTagIds(companyId, tagFields.tagIds);

      const logRef = await addDoc(collection(db, COLLECTIONS.ACTIVITY_LOGS), {
        companyId,
        userId: user.uid,
        action: 'edit_task',
        entityType: 'productionTask',
        entityId: editTask.id,
        before,
        after: afterSnapshot,
        reason: editReason.trim(),
        createdAt: serverTimestamp(),
      });

      const qtyBefore = editTask.quantityCompleted;
      const qtyAfter = qty;
      const eventType = qtyBefore !== qtyAfter
        ? PRODUCTION_EVENT_TYPES.QUANTITY_UPDATED
        : PRODUCTION_EVENT_TYPES.TASK_EDITED;

      logProductionEvent({
        companyId,
        taskId: editTask.id,
        eventType,
        userId: user.uid,
        task: { ...editTask, ...editedTaskSnapshot },
        reason: editReason.trim(),
        previousValue: changedFields.length
          ? JSON.stringify(Object.fromEntries(changedFields.map((key) => [key, before[key]])))
          : null,
        newValue: changedFields.length
          ? JSON.stringify(Object.fromEntries(changedFields.map((key) => [key, afterSnapshot[key]])))
          : null,
        metadata: { adminEdit: true, activityLogId: logRef.id, changedFields },
      });

      if (editForm.roomId && editForm.roomId !== editTask.roomId) {
        logProductionEvent({
          companyId,
          taskId: editTask.id,
          eventType: PRODUCTION_EVENT_TYPES.ROOM_CHANGED,
          userId: user.uid,
          task: editTask,
          previousValue: editTask.roomName || editTask.roomId,
          newValue: editForm.roomName || editForm.roomId,
          reason: editReason.trim(),
        });
      }

      toast(t('toast.taskUpdated'), 'success');
      setEditTask(null);
    } catch (err) {
      toast(t('toast.saveFailed'), 'error');
      console.error(err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) return;
    const taskId = archiveTarget.id;
    setArchiving(true);
    try {
      await updateDoc(doc(db, COLLECTIONS.PRODUCTION_TASKS, taskId), {
        archived: true,
        archivedAt: serverTimestamp(),
        archivedBy: user.uid,
        updatedAt: serverTimestamp(),
      });
      toast(t('toast.taskArchived'), 'success', {
        action: {
          label: t('common.undo'),
          onClick: () => updateDoc(doc(db, COLLECTIONS.PRODUCTION_TASKS, taskId), {
            archived: false,
            archivedAt: null,
            archivedBy: null,
            updatedAt: serverTimestamp(),
          }).catch(console.error),
        },
      });
      setArchiveTarget(null);
    } catch {
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setArchiving(false);
    }
  };

  const handleAbandonConfirm = async () => {
    if (!abandonTarget) return;
    setAbandoning(true);
    try {
      await updateDoc(doc(db, COLLECTIONS.PRODUCTION_TASKS, abandonTarget.id), {
        status: 'abandoned',
        pausedAt: null,
        updatedAt: serverTimestamp(),
        editedBy: user.uid,
        editedAt: serverTimestamp(),
        editReason: 'Abandoned by admin',
      });
      logProductionEvent({
        companyId,
        taskId: abandonTarget.id,
        eventType: PRODUCTION_EVENT_TYPES.TASK_CANCELLED,
        userId: user.uid,
        task: abandonTarget,
        reason: 'Abandoned by admin',
      });
      toast(t('toast.taskAbandoned'), 'success');
      setAbandonTarget(null);
    } catch {
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setAbandoning(false);
    }
  };

  const efSet = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));

  const handleCreateTag = async (name) => {
    const maxOrder = allTags.reduce((m, tg) => Math.max(m, tg.sortOrder || 0), 0);
    const docRef = await addDoc(collection(db, COLLECTIONS.TASK_TAGS), {
      companyId,
      name: normalizeTagName(name),
      color: DEFAULT_TAG_COLOR,
      isDefault: false,
      isActive: true,
      sortOrder: maxOrder + 1,
      createdBy: user.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  };

  return (
    <div className="tasks-page">
      <div className="page-header">
        <div>
          <h1>{t('admin.taskManagement')}</h1>
          <p className="page-header-sub">{t('tasksPage.subtitle')}</p>
        </div>
      </div>

      <div className="page-content-wide tasks-page-content">

        {/* Controls */}
        <div className="tasks-filterbar card">
          <div className="tasks-filterbar-top">
            <div className="search-box tasks-search">
              <Search size={15} aria-hidden="true" />
              <input
                className="search-input"
                placeholder={t('common.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label={t('common.search')}
              />
              {search && (
                <button type="button" className="tasks-search-clear" onClick={() => setSearch('')} aria-label={t('common.clear')}>
                  <X size={13} />
                </button>
              )}
            </div>
            <ExportButton
              options={[
                {
                  id: 'tasks',
                  label: t('export.csv'),
                  description: filteredTasks.length > 0
                    ? t('tasksPage.resultCount', { count: filteredTasks.length })
                    : t('tasksPage.exportUnavailable'),
                  disabled: filteredTasks.length === 0,
                  successMessage: t('tasksPage.exportSuccess', { count: filteredTasks.length }),
                  onExport: () => {
                    const csv = buildProductionTasksCSV(filteredTasks, currency);
                    downloadCSV(makeExportFilename('tasks', dateRange), csv);
                  },
                },
              ]}
            />
          </div>

          <div className="tasks-filterbar-bottom">
            <span className="tasks-filter-label">{t('common.filter')}</span>
            <div className="filter-segment" role="group" aria-label={t('a11y.dateRange')}>
              {DATE_PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`filter-seg-btn${datePreset === p ? ' active' : ''}`}
                  onClick={() => setDatePreset(p)}
                >
                  {t(`common.${p}`)}
                </button>
              ))}
            </div>

            <span className="tasks-filter-label">{t('taskTable.status')}</span>
            <div className="filter-segment" role="group" aria-label="Status">
              {['all', 'active', 'completed', 'abandoned'].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`filter-seg-btn${filterStatus === s ? ' active' : ''}`}
                  onClick={() => setFilterStatus(s)}
                >
                  {t(`common.${s}`)}
                </button>
              ))}
            </div>

            <button
              type="button"
              className={`tasks-filter-toggle${filterCustom ? ' active' : ''}`}
              onClick={() => setFilterCustom((v) => !v)}
              aria-pressed={filterCustom}
            >
              {t('tasksPage.customTaskName')}
            </button>

            <label className="tasks-tag-filter">
              <span className="tasks-filter-label">{t('tags.taskTags')}</span>
              <select
                className="form-control tasks-tag-filter-select"
                value={filterTagId}
                onChange={(e) => setFilterTagId(e.target.value)}
              >
                <option value="">{t('common.all')}</option>
                {allTags.filter((tg) => tg.isActive).map((tg) => (
                  <option key={tg.id} value={tg.id}>{tg.name}</option>
                ))}
              </select>
            </label>

            {!loading && (
              <span className="tasks-result-count">
                {t('tasksPage.resultCount', { count: filteredTasks.length })}
              </span>
            )}
          </div>

          <div className="tasks-filterbar-quick">
            <span className="tasks-filter-label">{t('tasksPage.quickFilters')}</span>
            <div className="tasks-quick-filters">
              {QUICK_FILTERS.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`filter-chip${quickFilter === key ? ' active' : ''}`}
                  onClick={() => toggleQuickFilter(key)}
                  aria-pressed={quickFilter === key}
                >
                  {t(`tasksPage.${key}`)}
                </button>
              ))}
            </div>
            {hasFiltersActive && (
              <button type="button" className="btn btn-sm btn-ghost tasks-reset-btn" onClick={resetFilters}>
                <RotateCcw size={13} aria-hidden="true" />
                {t('tasksPage.resetFilters')}
              </button>
            )}
          </div>
        </div>

        {/* Summary metrics */}
        {!loading && !loadError && filteredTasks.length > 0 && (
          <div className="tasks-summary-grid">
            <div className="tasks-summary-group card">
              <h2 className="tasks-summary-group-title">{t('tasksPage.summaryOverview')}</h2>
              <div className="tasks-summary-metrics">
                <div className="tasks-metric">
                  <span className="tasks-metric-label">{t('tasksPage.totalTasks')}</span>
                  <span className="tasks-metric-value tasks-metric-value--primary">{summaryStats.totalCount}</span>
                </div>
                <div className={`tasks-metric${summaryStats.activeRunning > 0 ? ' tasks-metric--highlight' : ''}`}>
                  <span className="tasks-metric-label">{t('tasksPage.activeNow')}</span>
                  <span className="tasks-metric-value">{summaryStats.activeRunning}</span>
                  {summaryStats.onBreakCount > 0 && (
                    <span className="tasks-metric-sub">{summaryStats.onBreakCount} {t('tasksPage.onBreak').toLowerCase()}</span>
                  )}
                </div>
                <div className="tasks-metric">
                  <span className="tasks-metric-label">{t('tasksPage.completed')}</span>
                  <span className="tasks-metric-value">{summaryStats.completedCount}</span>
                </div>
              </div>
            </div>

            <div className="tasks-summary-group card">
              <h2 className="tasks-summary-group-title">{t('tasksPage.summaryLabour')}</h2>
              <div className="tasks-summary-metrics">
                <div className="tasks-metric tasks-metric--cost">
                  <span className="tasks-metric-label">{t('tasksPage.labourCost')}</span>
                  <span className="tasks-metric-value">
                    {summaryStats.hasCost ? formatCurrency(summaryStats.totalCost, currency) : t('tasksPage.notRecorded')}
                  </span>
                </div>
                <div className="tasks-metric">
                  <span className="tasks-metric-label">{t('tasksPage.labourHours')}</span>
                  <span className="tasks-metric-value">
                    {summaryStats.totalHours > 0 ? `${summaryStats.totalHours.toFixed(1)}h` : t('tasksPage.notRecorded')}
                  </span>
                </div>
                {summaryStats.avgCost != null && (
                  <div className="tasks-metric">
                    <span className="tasks-metric-label">{t('tasksPage.avgCostPerTask')}</span>
                    <span className="tasks-metric-value">{formatCurrency(summaryStats.avgCost, currency)}</span>
                  </div>
                )}
                {summaryStats.avgCostPerUnit != null && (
                  <div className="tasks-metric">
                    <span className="tasks-metric-label">{t('tasksPage.costPerUnit')}</span>
                    <span className="tasks-metric-value">{formatCurrency(summaryStats.avgCostPerUnit, currency)}</span>
                  </div>
                )}
              </div>
            </div>

            {(summaryStats.needsReviewCount > 0 || summaryStats.longestActive != null) && (
              <div className="tasks-summary-group card tasks-summary-group--attention">
                <h2 className="tasks-summary-group-title">{t('tasksPage.summaryAttention')}</h2>
                <div className="tasks-summary-metrics">
                  {summaryStats.needsReviewCount > 0 && (
                    <div className="tasks-metric tasks-metric--warning">
                      <span className="tasks-metric-label">{t('tasksPage.needsReviewCount')}</span>
                      <span className="tasks-metric-value">{summaryStats.needsReviewCount}</span>
                    </div>
                  )}
                  {summaryStats.longestActive != null && summaryStats.activeRunning + summaryStats.onBreakCount > 0 && (
                    <div className="tasks-metric">
                      <span className="tasks-metric-label">{t('tasksPage.longestActive')}</span>
                      <span className="tasks-metric-value">{formatDuration(summaryStats.longestActive)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {filterStatus === 'all' && hasActiveTasks && !loading && (
          <p className="tasks-active-hint">
            <Info size={13} aria-hidden="true" />
            {t('tasksPage.activeAlwaysVisible')}
          </p>
        )}

        {/* Active now section */}
        {!loading && !loadError && hasActiveTasks && (
          <section className="tasks-active-section card" aria-labelledby="tasks-active-heading">
            <div className="tasks-active-header">
              <div className="tasks-active-header-title">
                <Activity size={16} className="tasks-active-icon" aria-hidden="true" />
                <h2 id="tasks-active-heading">{t('tasksPage.activeNowTitle')}</h2>
                <span className="tasks-active-count">{activeTasks.length}</span>
              </div>
              <p className="tasks-active-sub">{t('tasksPage.activeNowSub')}</p>
            </div>
            <ul className="tasks-active-list">
              {liveActiveViews.map((view) => (
                <ActiveTaskCard
                  key={view.task.id}
                  task={view.task}
                  liveView={view}
                  currency={currency}
                  t={t}
                  onEdit={openEdit}
                  onNotes={setNotesTask}
                  onAbandon={setAbandonTarget}
                />
              ))}
            </ul>
          </section>
        )}

        {/* Task list */}
        <div className="card tasks-list-card">
          <div className="tasks-list-header">
            <h2 className="tasks-list-title">
              <ClipboardList size={16} aria-hidden="true" />
              {quickFilter === 'activeOnly' ? t('tasksPage.activeOnly') : t('admin.taskManagement')}
            </h2>
            {!loading && historyTasks.length > 0 && (
              <span className="tasks-list-count">{t('tasksPage.resultCount', { count: historyTasks.length })}</span>
            )}
          </div>

          {loading && (
            <div className="tasks-state tasks-state--loading">
              <div className="spinner" aria-hidden="true" />
              <p>{t('tasksPage.loadingTasks')}</p>
            </div>
          )}

          {loadError && (
            <div className="tasks-state tasks-state--error">
              <AlertCircle size={28} aria-hidden="true" />
              <p>{t('tasksPage.loadError')}</p>
              <p className="tasks-state-sub">{t('tasksPage.loadErrorSub')}</p>
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => window.location.reload()}>
                {t('common.retry')}
              </button>
            </div>
          )}

          {!loading && !loadError && emptyState && (
            <div className="tasks-state">
              <ClipboardList size={28} strokeWidth={1.5} aria-hidden="true" />
              <p>{t(`tasksPage.${emptyState.key}`)}</p>
              <p className="tasks-state-sub">{t(`tasksPage.${emptyState.sub}`)}</p>
              {hasFiltersActive && (
                <button type="button" className="btn btn-sm btn-ghost" onClick={resetFilters}>
                  {t('tasksPage.resetFilters')}
                </button>
              )}
            </div>
          )}

          {!loading && !loadError && !emptyState && historyTasks.length === 0 && (
            <div className="tasks-state">
              <p>{quickFilter === 'activeOnly' ? t('tasksPage.noActiveTasks') : t('tasksPage.emptyDate')}</p>
              <p className="tasks-state-sub">
                {hasActiveTasks ? t('tasksPage.activeAlwaysVisible') : t('tasksPage.emptyDateSub')}
              </p>
            </div>
          )}

          {!loading && !loadError && historyTasks.length > 0 && (
            <>
              <div className="tasks-table-wrap tasks-desktop-only">
                <table className="tasks-table">
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>{t('taskTable.status')}</th>
                      <th>{t('taskTable.taskName')}</th>
                      <th style={{ width: 170 }}>{t('taskTable.startTime')}</th>
                      <th style={{ width: 140 }}>{t('taskTable.labourCost')}</th>
                      <th className="col-costunit" style={{ width: 100 }}>{t('taskTable.costUnit')}</th>
                      <th className="col-output" style={{ width: 160 }}>{t('taskTable.product')} / {t('taskTable.quantity')}</th>
                      <th className="col-meta" style={{ width: 150 }}>{t('taskTable.worker')} · {t('taskTable.room')}</th>
                      <th style={{ width: 100 }}>{t('taskTable.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyTasks.map((task) => (
                      <TaskTableRow
                        key={task.id}
                        task={task}
                        currency={currency}
                        t={t}
                        benchmark={benchmarks?.get?.(getBenchmarkKey(task))}
                        tagsById={tagsById}
                        onEdit={openEdit}
                        onNotes={setNotesTask}
                        onArchive={setArchiveTarget}
                        onAbandon={setAbandonTarget}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="tasks-mobile-list tasks-mobile-only">
                {historyTasks.map((task) => (
                  <TaskMobileCard
                    key={task.id}
                    task={task}
                    currency={currency}
                    t={t}
                    benchmark={benchmarks?.get?.(getBenchmarkKey(task))}
                    tagsById={tagsById}
                    onEdit={openEdit}
                    onNotes={setNotesTask}
                    onArchive={setArchiveTarget}
                    onAbandon={setAbandonTarget}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editTask && (
        <Modal
          title={t('admin.editTask')}
          onClose={() => setEditTask(null)}
          size="lg"
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setEditTask(null)} disabled={editLoading}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleEditSave} disabled={editLoading}>
                {editLoading ? <span className="spinner" /> : t('common.save')}
              </button>
            </>
          }
        >
          <div className="edit-grid">
            <div className="form-group">
              <label className="form-label">{t('production.taskName')}</label>
              <input className="form-control" value={editForm.taskName} onChange={(e) => efSet('taskName', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('production.customTaskName')}</label>
              <input className="form-control" value={editForm.customTaskName} onChange={(e) => efSet('customTaskName', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('production.startTime')}</label>
              <input type="datetime-local" className="form-control" value={editForm.startTime} onChange={(e) => efSet('startTime', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('production.endTime')}</label>
              <input type="datetime-local" className={`form-control ${editErrors.endTime ? 'is-error' : ''}`} value={editForm.endTime} onChange={(e) => efSet('endTime', e.target.value)} />
              {editErrors.endTime && <p className="form-error">{editErrors.endTime}</p>}
            </div>
            <div className="form-group">
              <label className="form-label">{t('production.teamSize')}</label>
              <input type="number" min="1" className={`form-control ${editErrors.teamSize ? 'is-error' : ''}`} value={editForm.teamSize} onChange={(e) => efSet('teamSize', e.target.value)} />
              {editErrors.teamSize && <p className="form-error">{editErrors.teamSize}</p>}
            </div>
            <div className="form-group">
              <label className="form-label">{t('production.productName')}</label>
              <input className="form-control" value={editForm.productName} onChange={(e) => efSet('productName', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('production.sku')}</label>
              <input className="form-control" value={editForm.sku} onChange={(e) => efSet('sku', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('production.lotNumber')}</label>
              <input className="form-control" value={editForm.lotNumber} onChange={(e) => efSet('lotNumber', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('production.quantityCompleted')}</label>
              <input type="number" min="0" className="form-control" value={editForm.quantityCompleted} onChange={(e) => efSet('quantityCompleted', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('production.unitType')}</label>
              <input className="form-control" value={editForm.unitType} onChange={(e) => efSet('unitType', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('production.downtimeMinutes')}</label>
              <input type="number" min="0" className={`form-control ${editErrors.downtimeMinutes ? 'is-error' : ''}`} value={editForm.downtimeMinutes} onChange={(e) => efSet('downtimeMinutes', e.target.value)} />
              {editErrors.downtimeMinutes && <p className="form-error">{editErrors.downtimeMinutes}</p>}
            </div>
            <div className="form-group">
              <label className="form-label">{t('production.downtimeReason')}</label>
              <input className="form-control" value={editForm.downtimeReason} onChange={(e) => efSet('downtimeReason', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('production.room')}</label>
              <select
                className="form-control"
                value={editForm.roomId}
                onChange={(e) => {
                  const area = areas.find((a) => a.id === e.target.value);
                  efSet('roomId', e.target.value);
                  efSet('roomName', area?.name ?? '');
                }}
              >
                <option value="">{t('common.optional')}</option>
                {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('tags.taskTags')}</label>
            <TaskTagPicker
              tags={allTags.filter((tg) => tg.isActive)}
              selectedIds={editForm.tagIds || []}
              onChange={(ids) => efSet('tagIds', ids)}
              allowCreate
              onCreateTag={handleCreateTag}
              suggestFromText={[editForm.notes, editForm.downtimeReason].filter(Boolean).join(' ')}
              usageCounts={tagUsageCounts}
              compact
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('production.notes')}</label>
            <textarea className="form-control" rows={2} value={editForm.notes} onChange={(e) => efSet('notes', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('admin.editReason')} <span className="required">*</span></label>
            <textarea
              className={`form-control ${editErrors.editReason ? 'is-error' : ''}`}
              placeholder={t('admin.editReasonPlaceholder')}
              rows={2}
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
            />
            {editErrors.editReason && <p className="form-error">{editErrors.editReason}</p>}
          </div>

          <div className="form-group" style={{ marginTop: 'var(--space-2)' }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <StickyNote size={13} />
              Task Notes
            </label>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', margin: '2px 0 var(--space-3)' }}>
              Notes help explain why this task performed differently. They do not change time or cost.
            </p>
            <TaskNotes
              taskId={editTask.id}
              taskCreatedBy={editTask.createdBy}
              task={editTask}
            />
          </div>
        </Modal>
      )}

      {archiveTarget && (
        <ConfirmDialog
          title={t('admin.archiveTask')}
          message={t('admin.archiveConfirm')}
          confirmLabel={t('admin.archiveTask')}
          onConfirm={handleArchiveConfirm}
          onCancel={() => setArchiveTarget(null)}
          loading={archiving}
        />
      )}

      {abandonTarget && (
        <ConfirmDialog
          title={t('admin.abandonTask')}
          message={t('admin.abandonConfirm')}
          confirmLabel={t('admin.abandonTask')}
          onConfirm={handleAbandonConfirm}
          onCancel={() => setAbandonTarget(null)}
          loading={abandoning}
        />
      )}

      {notesTask && (
        <Modal
          title={`Task Notes — ${notesTask.isCustomTaskName ? notesTask.customTaskName : notesTask.taskName}`}
          onClose={() => setNotesTask(null)}
          size="md"
          footer={
            <button className="btn btn-secondary" onClick={() => setNotesTask(null)}>
              {t('common.close')}
            </button>
          }
        >
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-4)' }}>
            Notes help explain why this task performed differently. They do not change time or cost.
          </p>
          <TaskNotes
            taskId={notesTask.id}
            taskCreatedBy={notesTask.createdBy}
            task={notesTask}
          />
        </Modal>
      )}
    </div>
  );
}
